// ════════════════════════════════════════════════════════════════
// REFER-A-FRIEND (added 2026-05-04)
// ────────────────────────────────────────────────────────────────
// Lightweight referral mechanism — the user gets a personal link
// like https://myveryown.page?ref=<userId>. When a friend lands
// via that link, we stash the referrer in localStorage and tag
// the next session creation with it. Backend can credit later.
//
// No backend dependency to use the share UI. The reward credit
// math runs server-side once the friend subscribes.
// ════════════════════════════════════════════════════════════════

const REF_KEY = 'mvo:ref-source';     // user ID of whoever referred you
const REF_LANDED = 'mvo:ref-landed';  // ts when ref link was first seen
const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Read ?ref=<id> from URL, persist with TTL, then strip from URL bar. */
export function captureReferralFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (!ref) return null;
  // Sanity-check: only short alphanumeric IDs (matches our user.id shape)
  if (!/^[a-zA-Z0-9_-]{4,40}$/.test(ref)) return null;
  try {
    localStorage.setItem(REF_KEY, ref);
    localStorage.setItem(REF_LANDED, String(Date.now()));
  } catch { /* private window, ignore */ }
  // Clean up the URL bar so the ref doesn't bleed everywhere
  try {
    params.delete('ref');
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  } catch { /* ignore */ }
  return ref;
}

export function getStoredReferral(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const ref = localStorage.getItem(REF_KEY);
  const landedRaw = localStorage.getItem(REF_LANDED);
  if (!ref || !landedRaw) return null;
  const landed = Number(landedRaw);
  if (!Number.isFinite(landed) || Date.now() - landed > REF_TTL_MS) {
    // Expired — purge
    try {
      localStorage.removeItem(REF_KEY);
      localStorage.removeItem(REF_LANDED);
    } catch { /* ignore */ }
    return null;
  }
  return ref;
}

export function clearReferral(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(REF_KEY);
    localStorage.removeItem(REF_LANDED);
  } catch { /* ignore */ }
}

/** Build the user's personal share link. Falls back to root if no userId. */
export function buildShareLink(userId: string | null | undefined, baseOrigin?: string): string {
  const origin = baseOrigin || (typeof window !== 'undefined' ? window.location.origin : 'https://myveryown.page');
  if (!userId) return origin;
  return `${origin}?ref=${encodeURIComponent(userId)}&utm_source=referral&utm_medium=link&utm_campaign=friend`;
}

/** Convenience: copy text to clipboard, return whether it worked. */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator === 'undefined') return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to legacy */ }
  // Legacy fallback
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

/** Pre-composed share message — paste-ready for X/Threads/iMessage. */
export function composeShareText(personaName?: string): string {
  if (personaName) {
    return `i've been using ${personaName} on MyVeryOwn — 20 ai companions where each one keeps its own private memory. yours actually remembers you. try it free:`;
  }
  return `MyVeryOwn — 20 ai companions where each one keeps its own private memory. they actually remember you. try it free:`;
}
